import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import {
  CATCH_UP_MS,
  LEAD_FLOOR_MS,
  fetchReminders,
  hasFired,
  markFired,
  momentFor,
  notificationPermission,
  onRemindersChanged,
  showReminder,
  type Reminder,
  type ReminderPhase,
} from '../lib/reminders';

/**
 * Raises the browser notification for a task reminder. Renders nothing.
 *
 * Mounted beside `<Routes>` so it survives every navigation -- a scheduler that
 * unmounted with the page would only ever fire while you happened to be looking
 * at the right screen, which is the opposite of the point. It no-ops until there
 * is a signed-in user, since the list it reads is scoped to one.
 *
 * ## One timer, always aimed at the next thing that matters
 *
 * This is lib/useDayBoundary.ts applied to a list instead of to midnight, and
 * that file's comment is worth reading first: it names the failure modes that
 * decide the shape here. Background tabs have their timers clamped. A laptop
 * suspended at 23:50 and opened at 09:00 fires a pending timeout whenever the
 * browser gets round to it. And `setInterval` is the wrong tool -- it drifts, and
 * spends a wakeup a minute to answer "no" almost every time.
 *
 * So: one `setTimeout` pointed at the next unfired moment, and three other
 * triggers -- focus, visibility, a reminder changed in this tab -- all funnelling
 * into the same idempotent `tick`. None of them has to be reliable alone, because
 * `tick` decides everything from the clock and the fired record rather than from
 * being called at the right instant. The read that would have shown you the stale
 * thing is the moment to fix it.
 *
 * When there is nothing left to fire, the timer aims at the server's `horizon`
 * instead and refetches there. That is what keeps a tab open for two days from
 * quietly running out of window.
 *
 * ## Three rules that are easy to get wrong
 *
 * **Mark before showing.** A throw out of `new Notification` must not leave the
 * moment unfired, or the next tick raises it again, and the one after that.
 *
 * **A late lead-in is judged on what is left, not on how late it is.** The
 * tempting rule -- drop any lead-in whose moment has passed -- is wrong here,
 * because a hidden tab's timers are clamped to roughly a wakeup a minute, so the
 * lead moment is seen late almost every time in exactly the tab this exists for.
 * What matters is whether there is still warning left to give, and the wording
 * counts the minutes actually remaining rather than promising the ten it was
 * scheduled for.
 *
 * **Nothing is marked while permission is ungranted.** Crossing moments off while
 * they cannot be delivered would mean granting permission at 09:00 silently
 * cancelled the 08:55 reminder instead of catching it.
 *
 * `StrictMode` double-invokes mount effects in dev. The fired record makes that
 * harmless -- which is itself a reason it lives in storage rather than in a ref
 * that a second mount would start empty.
 */

const PHASES: ReminderPhase[] = ['lead', 'due'];

/** A second of slack, so a timer firing a hair early cannot read the moment as
 *  still in the future and leave it until something else wakes the tab. The same
 *  allowance, for the same reason, as `useDayBoundary`'s. */
const SLACK_MS = 1000;

export function ReminderScheduler() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [horizon, setHorizon] = useState<number | null>(null);

  // Through refs so `tick` can be stable and the timer effect does not tear down
  // and reschedule on every render that happens to rebuild a callback.
  const remindersRef = useRef(reminders);
  remindersRef.current = reminders;
  const horizonRef = useRef(horizon);
  horizonRef.current = horizon;

  const openTask = useCallback((taskId: string) => navigate(`/tasks/${taskId}`), [navigate]);
  const openTaskRef = useRef(openTask);
  openTaskRef.current = openTask;

  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      const upcoming = await fetchReminders(signal);
      setReminders(upcoming.reminders);
      setHorizon(new Date(upcoming.horizon).getTime());
    } catch {
      // A reminder list that failed to load is not worth a toast: there is
      // nothing the reader can do about it, and the next focus tries again.
      // Leaving the previous list in place is deliberate -- a stale window still
      // fires the reminders it knows about.
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [user, load]);

  useEffect(() => {
    if (!user) return;

    let timer = 0;

    const tick = () => {
      window.clearTimeout(timer);

      // Ungranted permission means every moment stays where it is, unfired, so
      // granting it later still catches up. `tick` runs again on focus, which is
      // when a permission prompt has just been answered.
      const granted = notificationPermission() === 'granted';

      const now = Date.now();
      let next: number | null = null;

      // Flattened to one list of moments and sorted, so the two phases of a
      // reminder are judged in the order they were meant to happen -- and so the
      // first future moment reached is the earliest one, which is what the timer
      // below has to aim at.
      const moments = remindersRef.current
        .flatMap((reminder) => PHASES.map((phase) => ({ reminder, phase })))
        .sort((a, b) => momentFor(a.reminder, a.phase) - momentFor(b.reminder, b.phase));

      for (const { reminder, phase } of moments) {
        if (hasFired(reminder, phase)) continue;

        const at = momentFor(reminder, phase);
        if (at > now) {
          if (next === null || at < next) next = at;
          continue;
        }

        if (!granted) continue;

        const late = now - at;
        const remainingMs = new Date(reminder.remindAt).getTime() - now;

        // Both phases can be too late to be worth raising, but they are late
        // about different things. A lead-in is a warning, so what matters is how
        // much warning is left -- not how long ago the moment to give it was, which
        // a clamped timer in a hidden tab gets wrong routinely. The moment itself
        // is an announcement, and an announcement of something that was this
        // morning is noise.
        const pointless =
          phase === 'lead' ? remainingMs < LEAD_FLOOR_MS : late > CATCH_UP_MS;

        if (pointless) {
          markFired(reminder, phase);
          continue;
        }

        // Marked first. A throw below must not leave this to be retried forever.
        markFired(reminder, phase);
        try {
          showReminder(reminder, phase, {
            // A minute's grace before the wording changes: "Now" is true enough
            // for a timer that fired a few seconds late, and "Was 14:30" at
            // 14:30 would read as a bug.
            late: late > 60_000,
            remainingMs,
            onOpen: (taskId) => openTaskRef.current(taskId),
          });
        } catch {
          /* A browser that refuses to construct one is not worth retrying. */
        }
      }

      // Nothing left to fire means the window itself is what runs out next, so
      // the timer aims at the horizon and refetches rather than going quiet.
      const horizonAt = horizonRef.current;
      const target = next ?? (horizonAt !== null && horizonAt > now ? horizonAt : null);
      if (target === null) return;

      timer = window.setTimeout(
        () => {
          if (next === null) void load();
          else tick();
        },
        target - Date.now() + SLACK_MS,
      );
    };

    /**
     * A timer may have been missed, clamped or slept through, but what we hold
     * is still the truth as far as we know it. Decide from it straight away --
     * a reminder waiting to be caught up must not sit behind a network round
     * trip -- and refresh behind that.
     */
    const recheck = () => {
      tick();
      void load();
    };

    /**
     * The list we hold is known to be out of date, because something in this tab
     * just changed a reminder. Deliberately does *not* tick: deciding from a list
     * we know is stale is how a reminder moved from 3pm to 5pm raises its old
     * 2:50 lead-in on the way past. Load, and let the new state tick.
     */
    const resync = () => {
      void load();
    };

    tick();
    window.addEventListener('focus', recheck);
    document.addEventListener('visibilitychange', recheck);
    const stopListening = onRemindersChanged(resync);

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('focus', recheck);
      document.removeEventListener('visibilitychange', recheck);
      stopListening();
    };
  }, [user, reminders, horizon, load]);

  return null;
}
